import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../hooks/useApi';

export default function ToolConnectionsPage() {
  const { api } = useApi();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState({});
  const [error, setError] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api('/admin/tools/sync-status');
      setStatus(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const triggerFullSync = async (toolId) => {
    setSyncLoading(prev => ({ ...prev, [toolId]: true }));
    try {
      await api(`/admin/tools/${toolId}/sync-all-tenants`, { method: 'POST' });
      alert(`Vollsync für ${toolId} gestartet. Läuft im Hintergrund.`);
    } catch (e) {
      alert(`Fehler: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSyncLoading(prev => ({ ...prev, [toolId]: false }));
    }
  };

  const statusColor = (tool) => {
    if (!tool) return '#8888aa';
    if (tool.reachable && tool.http_status < 300) return '#5bf05b';
    if (tool.reachable) return '#f0c05b';
    return '#f05b5b';
  };

  const statusLabel = (tool) => {
    if (!tool) return '–';
    if (tool.error) return `Fehler: ${tool.error.substring(0, 60)}`;
    if (tool.reachable && tool.http_status < 300) return `Live (${tool.latency_ms}ms)`;
    if (tool.reachable) return `HTTP ${tool.http_status} (${tool.latency_ms}ms)`;
    return 'Nicht erreichbar';
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
          Tool-Verbindungen (PINFRA-540)
        </h2>
        <button
          onClick={loadStatus}
          disabled={loading}
          style={{
            padding: '6px 14px', fontSize: 12, fontWeight: 600,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 6, color: 'var(--text-secondary)', cursor: 'pointer',
          }}
        >
          {loading ? 'Prüfe…' : '↻ Neu prüfen'}
        </button>
      </div>

      {error && (
        <div style={{
          padding: 12, background: '#f05b5b22', border: '1px solid #f05b5b44',
          borderRadius: 8, color: '#f05b5b', marginBottom: 16, fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {status && (
        <>
          <div style={{
            padding: '10px 14px', background: 'var(--bg-card)',
            border: '1px solid var(--border)', borderRadius: 8,
            fontSize: 12, color: 'var(--text-muted)', marginBottom: 20,
          }}>
            Portal-Base-URL: <code style={{ color: 'var(--accent)' }}>{status.portal_base_url}</code>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {status.tools.map(tool => (
              <div key={tool.tool_id} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '16px 20px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: statusColor(tool), flexShrink: 0,
                  }} />
                  <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>
                    {tool.label || tool.tool_id.toUpperCase()}
                  </span>
                  <span style={{
                    fontSize: 12, padding: '2px 8px',
                    background: tool.reachable ? '#5bf05b22' : '#f05b5b22',
                    color: statusColor(tool), borderRadius: 4, fontWeight: 600,
                  }}>
                    {statusLabel(tool)}
                  </span>
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                    background: 'var(--bg-surface)', color: 'var(--text-muted)',
                    border: '1px solid var(--border)',
                  }}>
                    {tool.kind === 'monitor' ? 'Monitor' : 'Sync'}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '4px 8px', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Sync-URL:</span>
                  <code style={{ color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
                    {tool.sync_base_url}
                  </code>
                  <span style={{ color: 'var(--text-muted)' }}>Health-URL:</span>
                  <code style={{ color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
                    {tool.health_url || '–'}
                  </code>
                  {tool.http_status && (
                    <>
                      <span style={{ color: 'var(--text-muted)' }}>HTTP-Status:</span>
                      <span style={{ color: 'var(--text-secondary)' }}>{tool.http_status}</span>
                    </>
                  )}
                  {tool.latency_ms !== null && (
                    <>
                      <span style={{ color: 'var(--text-muted)' }}>Latenz:</span>
                      <span style={{ color: 'var(--text-secondary)' }}>{tool.latency_ms}ms</span>
                    </>
                  )}
                </div>

                {/* Vollsync nur fuer echte Tenant-Sync-Tools; monitor-only Eintraege
                    (Health-Ueberwachung aus der Registry) zeigen keinen Sync-Button. */}
                {tool.kind !== 'monitor' && (
                  <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => triggerFullSync(tool.tool_id)}
                      disabled={!tool.reachable || syncLoading[tool.tool_id]}
                      title={!tool.reachable ? 'Tool nicht erreichbar' : 'Alle Tenants synchronisieren'}
                      style={{
                        padding: '6px 14px', fontSize: 12, fontWeight: 600,
                        background: tool.reachable ? 'var(--accent)' : 'var(--bg-surface)',
                        color: tool.reachable ? '#fff' : 'var(--text-muted)',
                        border: 'none', borderRadius: 6,
                        cursor: tool.reachable ? 'pointer' : 'not-allowed',
                        opacity: syncLoading[tool.tool_id] ? 0.7 : 1,
                      }}
                    >
                      {syncLoading[tool.tool_id] ? 'Sync läuft…' : '⟳ Vollsync (alle Tenants)'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{
            marginTop: 24, padding: '12px 16px',
            background: '#5b6af022', border: '1px solid #5b6af044',
            borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)',
          }}>
            <strong style={{ color: 'var(--text-primary)' }}>Hinweis Realm Model B (PINFRA-539):</strong>
            {' '}Tenant-Sync überträgt Mandant-Stammdaten und Lizenzen. Keycloak-Auth-Cutover
            (keycloak_enabled=true) erfolgt separat nach PINFRA-368 E2E-Verifikation.
            {' '}P4I docker-compose benötigt{' '}
            <code>PORTAL_INTERNAL_URL=https://portal.secaas.it/api/internal</code>.
          </div>
        </>
      )}
    </div>
  );
}
